import { tv, VariantProps } from "tailwind-variants";
import InlineButton from "@/components/ui/inline-button";
import { FaArrowRight } from "react-icons/fa6";
import Image from "next/image";
import Arrow from "@/components/ui/arrow";
import {
  LinkBox,
  LinkOverlay,
} from "@/components/ui/link-overlay/link-overlay";
import Link from "next/link";

const resourcesTextCardVariants = tv({
  slots: {
    container: "",
    image: "",
    imageContainer: "",
    title: "",
    titleSpan: "",
    text: "",
    infoContainer: "",
    leftInfo: "",
    rightInfo: "",
    inlineButtonCta: "",
    icon: "",
  },
  variants: {
    cardType: {
      "icon-feature-vr": {
        container:
          "grid h-fit items-start gap-1 py-3 text-center md:flex md:flex-row md:items-center md:gap-3 md:py-2 md:text-start lg:gap-4",
        icon: "mx-auto size-10 w-fit md:m-0 md:size-16 lg:size-20",
        title:
          "w-full font-montserrat text-subheading-3-500 text-neutral-500 lg:text-subheading-2",
      },
      "icon-feature-ion": {
        container:
          "flex flex-col justify-start gap-2 rounded-lg bg-primary-100 p-4 text-center lg:gap-4 lg:p-10",
        icon: "mx-auto size-10 md:size-16 lg:size-20",
        title:
          "font-montserrat text-subheading-3-500 text-neutral-600 lg:text-subheading-2",
        text: "line-clamp-5 font-roboto text-body-1-300 text-neutral-500",
      },
      "icon-benefit": {
        container: "grid gap-2 rounded-3xl bg-transparent md:gap-4",
        icon: "size-10 md:size-16 lg:size-20",
        title:
          "font-montserrat text-subheading-3-500 text-secondary-500 lg:text-subheading-2",
        text: "-mt-2 font-roboto text-body-1-300 text-neutral-500",
      },
      text: {
        container: "grid gap-2 md:gap-4",
        title:
          "text-subheading-4-600 md:text-subheading-2-600 font-montserrat text-secondary-600",
        text: "font-roboto text-body-1-400 text-neutral-600",
        inlineButtonCta: "group",
      },
      "inline-button": {
        container: "group grid grid-cols-1 gap-2",
        title: "font-montserrat text-subheading-3-500 text-neutral-600",
        text: "line-clamp-2 font-roboto text-body-1-300 text-neutral-500 md:line-clamp-3",
        inlineButtonCta:
          "group -mt-2 gap-2 no-underline group-hover:underline md:mt-0",
      },
      image: {
        container: "group grid grid-cols-1 gap-2 md:hover:bg-neutral-100",
        imageContainer: "relative aspect-[360/180]",
        image: "object-cover",
        title:
          "font-montserrat text-subheading-3-400 text-neutral-500 group-hover:underline group-hover:underline-offset-2 md:text-subheading-3-500",
        titleSpan: "line-clamp-2 md:line-clamp-3",
        text: "font-roboto text-body-1-300 text-neutral-500",
        infoContainer: "flex flex-row justify-between",
        leftInfo: "font-roboto text-body-2-300 text-neutral-500",
        rightInfo: "font-roboto text-body-2-300 text-neutral-500",
      },
      thumbnail: {
        container: "grid",
        imageContainer:
          "relative mb-2 aspect-[171/224] border border-neutral-100 shadow-[2px_8px_16px_0px_rgba(0,65,66,0.1)] md:mb-4 md:aspect-[196/255]",
        image: "object-cover",
        title:
          "font-montserrat text-subheading-3-400 text-neutral-500 md:line-clamp-none md:text-subheading-3-500",
        titleSpan: "line-clamp-2 md:line-clamp-3",
      },
      "no-image": {
        container:
          "transition-color group grid grid-cols-1 gap-2 bg-white ease-out hover:bg-neutral-100",
        title:
          "text-subheading-4-500 font-montserrat text-neutral-600 group-hover:underline group-hover:underline-offset-2 md:text-subheading-3-500",
        titleSpan: "line-clamp-2 md:line-clamp-3",
        text: "line-clamp-2 font-roboto text-body-1-300 text-neutral-600",
        infoContainer: "flex flex-row justify-between",
        leftInfo: "font-roboto text-body-2-300 text-neutral-500",
        rightInfo: "font-roboto text-body-2-300 text-neutral-500",
        image: "hidden",
        icon: "hidden",
      },
    },
    link: {
      "link-left": "",
      "link-right": "",
    },
  },
  compoundVariants: [
    {
      cardType: "image",
      link: "link-left",
      class: {
        container: "group grid grid-cols-1 gap-2 bg-transparent md:gap-4",
        imageContainer:
          "relative aspect-[358/240] overflow-hidden rounded-lg md:aspect-[407/305]",
        image: "object-cover",
        title:
          "font-montserrat text-subheading-3-500 text-neutral-600 !no-underline md:text-subheading-2",
        text: "font-roboto text-body-1-300 text-neutral-500 md:-mt-3 md:text-neutral-600",
        inlineButtonCta:
          "group h-fit gap-2 no-underline group-hover:underline md:-mt-3",
      },
    },
  ],
  defaultVariants: {
    cardType: "text",
    link: "link-left",
  },
});

export type ResourcesTextCardVariants = VariantProps<
  typeof resourcesTextCardVariants
>;

export type CardTypes = Pick<ResourcesTextCardVariants, "cardType">["cardType"];

type StyleOverrides = {
  container?: string;
  title?: string;
  titleSpan?: string;
  text?: string;
  inlineButtonCta?: string;
  infoContainer?: string;
  leftInfo?: string;
  rightInfo?: string;
  image?: string;
  imageContainer?: string;
  icon?: string;
};

export type ResourcesTextCardProps = {
  title?: string;
  text?: string;
  target?: string;
  styleOverrides?: StyleOverrides;
} & ResourcesTextCardVariants &
  (
    | { cardType?: "text" }
    | {
        cardType: "icon-feature-vr" | "icon-feature-ion" | "icon-benefit";
        icon: React.FC<React.SVGProps<SVGSVGElement>>;
      }
    | {
        cardType: "thumbnail";
        label: { text: string; href: string };
        image: { src: string; alt: string };
        titleHref: string;
      }
    | {
        cardType: "image";
        link: "link-right";
        leftInfo: string;
        rightInfo: string;
        titleHref: string;
        image: { src: string; alt: string };
      }
    | {
        cardType: "no-image";
        leftInfo: string;
        rightInfo: string;
        titleHref: string;
      }
    | {
        cardType: "inline-button";
        inlineButtonCta: { text: string; href: string };
      }
    | {
        cardType: "image";
        link: "link-left";
        inlineButtonCta: { text: string; href: string };
        image: { src: string; alt: string };
      }
  );

export default function ResourcesTextCard({
  title,
  text,
  cardType = "text",
  styleOverrides,
  target,
  ...props
}: ResourcesTextCardProps) {
  const {
    container,
    title: titleStyle,
    titleSpan: titleSpanStyle,
    text: textStyle,
    inlineButtonCta: inlineButtonCtaStyle,
    infoContainer: infoContainerStyle,
    leftInfo: leftInfoStyle,
    rightInfo: rightInfoStyle,
    image: imageStyle,
    imageContainer: imageContainerStyle,
    icon: iconStyle,
  } = resourcesTextCardVariants({
    cardType,
    link: "link" in props ? props.link : undefined,
  });

  const Icon = "icon" in props ? props.icon : null;

  return (
    <LinkBox asChild>
      <div className={container({ className: styleOverrides?.container })}>
        {(cardType === "icon-feature-vr" ||
          cardType === "icon-feature-ion" ||
          cardType === "icon-benefit") &&
          Icon && (
            <Icon className={iconStyle({ className: styleOverrides?.icon })} />
          )}

        {cardType === "image" && "image" in props && props.image && (
          <div
            className={imageContainerStyle({
              className: styleOverrides?.imageContainer,
            })}
          >
            <Image
              src={props.image.src}
              alt={props.image.alt}
              fill
              className={imageStyle({ className: styleOverrides?.image })}
            />
          </div>
        )}
        {cardType === "thumbnail" && "image" in props && props.image && (
          <div
            className={imageContainerStyle({
              className: styleOverrides?.imageContainer,
            })}
          >
            <Image
              src={props.image.src}
              alt={props.image.alt}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              className={imageStyle({ className: styleOverrides?.image })}
            />
          </div>
        )}
        {title && (
          <>
            {(cardType === "image" &&
              "link" in props &&
              props.link === "link-right" &&
              "titleHref" in props) ||
            (cardType === "no-image" && "titleHref" in props) ||
            (cardType === "thumbnail" && "titleHref" in props) ? (
              <LinkOverlay
                target={target}
                href={props.titleHref}
              >
                <h3
                  className={titleStyle({ className: styleOverrides?.title })}
                >
                  <span
                    className={titleSpanStyle({
                      className: styleOverrides?.titleSpan,
                    })}
                  >
                    {title}
                  </span>
                </h3>
              </LinkOverlay>
            ) : (
              <h3 className={titleStyle({ className: styleOverrides?.title })}>
                {title}
              </h3>
            )}
          </>
        )}
        {text && (
          <p className={textStyle({ className: styleOverrides?.text })}>
            {text}
          </p>
        )}
        {((cardType === "image" &&
          "link" in props &&
          props.link === "link-right") ||
          cardType === "no-image") && (
          <div
            className={infoContainerStyle({
              className: styleOverrides?.infoContainer,
            })}
          >
            {"leftInfo" in props && props.leftInfo && (
              <p
                className={leftInfoStyle({
                  className: styleOverrides?.leftInfo,
                })}
              >
                {props.leftInfo}
              </p>
            )}
            {"rightInfo" in props && props.rightInfo && (
              <p
                className={rightInfoStyle({
                  className: styleOverrides?.rightInfo,
                })}
              >
                {props.rightInfo}
              </p>
            )}
          </div>
        )}

        {cardType === "thumbnail" && (
          <div className="flex h-fit flex-row items-end justify-between">
            {"label" in props && props.label && (
              <Link
                className="rounded bg-primary-100 px-4 py-1 font-roboto text-body-2-400 text-secondary-500 md:px-4"
                href={props.label.href}
              >
                {props.label.text}
              </Link>
            )}
            {"titleHref" in props && props.titleHref && (
              <Arrow
                className="size-8 md:size-10"
                direction={"right"}
                buttonType={"filled"}
                color={"teal"}
                href={props.titleHref}
              />
            )}
          </div>
        )}

        {((cardType === "inline-button" && "inlineButtonCta" in props) ||
          (cardType === "image" &&
            "link" in props &&
            props.link === "link-left" &&
            "inlineButtonCta" in props)) && (
          <InlineButton
            className={inlineButtonCtaStyle({
              className: styleOverrides?.inlineButtonCta,
            })}
            size="md"
            color={cardType === "inline-button" ? "orange" : "teal"}
            href={props.inlineButtonCta.href}
          >
            {props.inlineButtonCta.text}
            <FaArrowRight className="transition-transform group-hover:translate-x-1" />
          </InlineButton>
        )}
      </div>
    </LinkBox>
  );
}
